import React, { useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Project, PunchItem, PUNCH_VERIFIED_STATUS } from '../../types';
import { getProjectPunchItems } from '../../services/punchService';

/**
 * Dashboard widget summarising a project's punch list. Renders only when the
 * Punch List module is enabled. Clicking the Open Items count navigates to the
 * Punch List tab via onOpen.
 */
export default function PunchListWidget({ pca, project, onOpen }: {
  pca: IPublicClientApplication;
  project: Project;
  onOpen: () => void;
}) {
  const [items, setItems] = useState<PunchItem[] | null>(null);

  useEffect(() => {
    if (!project.dagrun_pm_module_punchlist) return;
    let cancelled = false;
    getProjectPunchItems(pca, project.dagrun_pm_projectid)
      .then(list => { if (!cancelled) setItems(list); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [pca, project.dagrun_pm_projectid, project.dagrun_pm_module_punchlist]);

  if (!project.dagrun_pm_module_punchlist) return null;

  const isVerified = (p: PunchItem) => p.dagrun_pm_status === PUNCH_VERIFIED_STATUS || !!p.dagrun_pm_verifiedon;
  const total = items?.length ?? 0;
  const verified = items ? items.filter(isVerified).length : 0;
  const open = total - verified;
  const pctVerified = total > 0 ? Math.round((verified / total) * 100) : 0;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const overdue = items
    ? items.filter(p => !isVerified(p) && p.dagrun_pm_duedate && new Date(p.dagrun_pm_duedate) < today).length
    : 0;

  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div className="eyebrow">PUNCH LIST</div>
        {overdue > 0 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: '#FEE2E2', color: '#991B1B',
            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
            fontFamily: 'var(--font-ui)',
          }}>
            ⚑ {overdue} overdue
          </span>
        )}
      </div>

      {items === null ? (
        <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>Loading…</div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24 }}>
            <button
              type="button"
              onClick={onOpen}
              title="Open the Punch List"
              style={{
                background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
              }}
            >
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 200, fontSize: 32, color: 'var(--brand)', lineHeight: 1 }}>
                {open}
              </div>
              <div className="eyebrow" style={{ marginTop: 4, color: 'var(--brand)' }}>OPEN ITEMS →</div>
            </button>
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 200, fontSize: 32, color: 'var(--ink)', lineHeight: 1 }}>
                {pctVerified}%
              </div>
              <div className="eyebrow" style={{ marginTop: 4 }}>VERIFIED</div>
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={{ background: 'var(--surface-2)', borderRadius: 3, height: 6, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pctVerified}%`, background: '#16A34A', transition: 'width 0.2s' }} />
            </div>
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>
              {verified} of {total} verified
            </div>
          </div>
        </>
      )}
    </div>
  );
}
